import React, { useState, useEffect, useRef } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import CollapsibleSection from './CollapsibleSection';
import '../styles/about-us.css';

const AboutUs = () => {
  const { t, currentLanguage } = useLanguage();
  const { user } = useAuth();
  const [isPlayingQuote, setIsPlayingQuote] = useState(false);
  const [schedule, setSchedule] = useState(null); // null = not loaded, [] = loaded
  const [scheduleError, setScheduleError] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    const loadSchedule = async () => {
      try {
        const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
        const response = await fetch(`${API_BASE}/api/rehearsals?operation=getSchedule`);
        if (!response.ok) {
          throw new Error('Failed to load rehearsal schedule');
        }
        setSchedule(await response.json());
      } catch (error) {
        console.error('Error loading rehearsal schedule:', error);
        setScheduleError(true);
      }
    };
    loadSchedule();
  }, []);

  // day_of_week: 0=Sunday ... 6=Saturday. Aug 1 2021 was a Sunday.
  const formatScheduleDay = (dayOfWeek) => {
    const date = new Date(Date.UTC(2021, 7, 1 + dayOfWeek));
    const day = date.toLocaleDateString(currentLanguage, { weekday: 'long', timeZone: 'UTC' });
    // Some locales (fr, ru) return lowercase day names; the day starts the sentence
    return day.charAt(0).toUpperCase() + day.slice(1);
  };

  const formatScheduleTime = (timeString) => {
    if (!timeString) return '';
    const [hours, minutes] = timeString.split(':');
    const date = new Date(2000, 0, 1, parseInt(hours, 10), parseInt(minutes, 10));
    return date.toLocaleTimeString(currentLanguage, { hour: 'numeric', minute: '2-digit' });
  };

  // Determine if section should be open: always open for now
  const hasSignedInBefore = localStorage.getItem('musiker-has-signed-in') === 'true';
  const shouldBeOpen = true; // Always show expanded

  const playRandomOlgaQuote = async () => {
    if (isPlayingQuote) {
      // Stop current audio if playing
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
      setIsPlayingQuote(false);
      return;
    }

    try {
      setIsPlayingQuote(true);
      
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(`${API_BASE}/api/olga-quotes?operation=getRandomQuote`);
      
      if (!response.ok) {
        throw new Error('Failed to get random quote');
      }
      
      const data = await response.json();
      
      if (data.success && data.file) {
        // Create and play audio
        if (audioRef.current) {
          audioRef.current.src = data.file.url;
          audioRef.current.onended = () => setIsPlayingQuote(false);
          audioRef.current.onerror = () => setIsPlayingQuote(false);
          
          await audioRef.current.play();
        }
      } else {
        console.log('No quotes available yet - upload some audio files to olga-quotes/ folder!');
        setIsPlayingQuote(false);
      }
    } catch (error) {
      console.error('Error playing Olga quote:', error);
      setIsPlayingQuote(false);
    }
  };

  return (
    <CollapsibleSection 
      title={`🎵 ${t('aboutUs.title')}`}
      defaultExpanded={shouldBeOpen}
      className="about-us-section"
    >
      <div className="about-us-content">
        <div className="about-us-main">
          <div className="choir-text">
            <p>{t('aboutUs.choirDescription')}</p>
            <p>{t('aboutUs.rehearsalInfo')}</p>
            {schedule ? (
              schedule.map((item) => (
                <p key={item.id}>
                  {t('aboutUs.rehearsalSchedule.line', {
                    day: formatScheduleDay(item.day_of_week),
                    time: formatScheduleTime(item.time)
                  })}
                </p>
              ))
            ) : scheduleError ? (
              <>
                <p>{t('aboutUs.rehearsalSchedule.thursday')}</p>
                <p>{t('aboutUs.rehearsalSchedule.sunday')}</p>
              </>
            ) : null}
            <p>{t('aboutUs.pricing')}</p>
          </div>
          <div className="choir-image-container">
            <img 
              src="/images/choir-photo.png" 
              alt="Choir"
              className="choir-image"
            />
          </div>
        </div>
        
        <div className="about-us-divider"></div>
        
        <div className="about-us-director">
          <h3>{t('aboutUs.meetOurDirector')}, {t('aboutUs.directorName')}</h3>
          <div 
            className={`director-image-container ${isPlayingQuote ? 'playing' : ''}`}
            onClick={playRandomOlgaQuote}
          >
            <img 
              src="/images/director-olga.jpg" 
              alt="Olga"
              className="director-image"
            />
            {isPlayingQuote && <div className="playing-indicator">🔊</div>}
          </div>
          <audio ref={audioRef} preload="none" />
          <p className="director-text">{t('aboutUs.directorDescription')}</p>
        </div>
      </div>
    </CollapsibleSection>
  );
};

export default AboutUs;